import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, FileText, Loader2, Settings, BarChart3, History } from 'lucide-react';
import { useEPSESite } from '@/hooks/useEPSESites';
import { useEPSEQuarterData } from '@/hooks/useEPSEQuarterData';
import { useEPSEProfiles } from '@/hooks/useEPSEProfiles';
import { useEPSESimulations } from '@/hooks/useEPSESimulations';
import { useEPSELiveSimulation } from '@/hooks/useEPSELiveSimulation';
import { EPSEInputPanel } from '@/components/epse/EPSEInputPanel';
import { EPSESimulationDashboard } from '@/components/epse/EPSESimulationDashboard';
import { EPSESavedSimulations } from '@/components/epse/EPSESavedSimulations';
import { EPSEKPICards } from '@/components/epse/EPSEKPICards';
import { EPSESOCChart } from '@/components/epse/EPSESOCChart';
import { BESSConfig, SyntheticProfileConfig } from '@/types/epse';
import { getDefaultBESSConfig } from '@/lib/epseSimulationEngine';
import { getDefaultSyntheticConfig, generateDailyProfile } from '@/lib/epseSyntheticProfile';
import { calculateEnergyBalance } from '@/lib/epseEnergyBalance';
import { toast } from 'sonner';

export default function CRMEPSESiteDetail() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  
  const { data: site, isLoading: siteLoading } = useEPSESite(siteId);
  const { quarterData, isLoading: dataLoading, uploadQuarterData } = useEPSEQuarterData(siteId);
  const { profiles, generateProfiles } = useEPSEProfiles(siteId);
  const { simulations, isLoading: simulationsLoading, runSimulation, deleteSimulation } = useEPSESimulations(siteId);
  
  // State
  const [activeTab, setActiveTab] = useState('input');
  const [profileMode, setProfileMode] = useState<'measured' | 'synthetic'>(
    site?.profile_mode || 'synthetic'
  );
  const [bessConfig, setBessConfig] = useState<BESSConfig>(getDefaultBESSConfig());
  const [syntheticConfig, setSyntheticConfig] = useState<SyntheticProfileConfig>(
    getDefaultSyntheticConfig(site?.annual_kwh_total || 500000, site?.connection_kw_limit || 150)
  );
  const [dispatchMode, setDispatchMode] = useState<'rule_based' | 'perfect_foresight'>('rule_based');
  const [selectedSeason, setSelectedSeason] = useState<'winter' | 'zomer'>('winter');
  const [viewingSimulation, setViewingSimulation] = useState<any>(null);
  
  // NEW: Optional module toggles
  const [bessEnabled, setBessEnabled] = useState<boolean>(true);
  const [pvEnabled, setPvEnabled] = useState<boolean>(true);
  
  // NEW: Unit override for data upload
  const [unitOverride, setUnitOverride] = useState<'auto' | 'kW' | 'kWh'>('auto');
  const [detectedUnit, setDetectedUnit] = useState<'kW' | 'kWh' | undefined>(undefined);
  
  // Generate synthetic profile data
  const syntheticProfileData = useMemo(() => {
    if (profileMode !== 'synthetic') return null;
    return generateDailyProfile(syntheticConfig, selectedSeason, false);
  }, [profileMode, syntheticConfig, selectedSeason]);
  
  // Calculate energy balance
  const energyBalance = useMemo(() => {
    if (!syntheticProfileData) return null;
    return calculateEnergyBalance(syntheticProfileData, syntheticConfig, bessConfig, selectedSeason);
  }, [syntheticProfileData, syntheticConfig, bessConfig, selectedSeason]);
  
  // Peak kW
  const peakKw = useMemo(() => {
    if (syntheticConfig.use_manual_peak) return syntheticConfig.peak_kw_manual;
    const dailyKwh = syntheticConfig.annual_kwh / 365;
    return Math.min((dailyKwh / 24) / 0.6, syntheticConfig.connection_kw_limit * 1.2);
  }, [syntheticConfig]);
  
  // Measured profile data
  const measuredProfileData = useMemo(() => {
    if (!quarterData || quarterData.length === 0) return null;
    return quarterData.map(q => q.kw_value);
  }, [quarterData]);
  
  // Live profile data
  const liveProfileData = profileMode === 'synthetic' ? syntheticProfileData : measuredProfileData;
  const connectionLimit = site?.connection_kw_limit || syntheticConfig.connection_kw_limit;
  
  // Live simulation hook - pass bessEnabled to conditionally skip BESS
  const { chartData: liveChartData, kpis: liveKpis, isCalculating } = useEPSELiveSimulation(
    liveProfileData,
    bessEnabled ? bessConfig : null,
    connectionLimit
  );
  
  // Handlers
  const handleDataUpload = async (data: any, detectedUnitFromUpload?: 'kW' | 'kWh') => {
    if (!siteId) return;
    
    // Store detected unit
    if (detectedUnitFromUpload) {
      setDetectedUnit(detectedUnitFromUpload);
    }
    
    await uploadQuarterData.mutateAsync({
      siteId,
      data,
      schedule: site?.operating_schedule,
    });
    
    if (data.length > 0) {
      await generateProfiles.mutateAsync({
        siteId,
        quarterData: data,
        annualKwh: site?.annual_kwh_total ?? undefined,
      });
    }
  };
  
  const handleSaveSimulation = async (name: string) => {
    if (!siteId) return;
    if (profileMode === 'measured' && (!quarterData || quarterData.length === 0)) {
      toast.error('Upload eerst kwartierdata om een simulatie op te slaan');
      return;
    }
    
    await runSimulation.mutateAsync({
      siteId,
      name,
      bessConfig: bessEnabled ? bessConfig : null,
      dispatchMode,
      quarterData: profileMode === 'measured' ? quarterData! : [],
      connectionLimit,
    });
    
    toast.success('Simulatie opgeslagen');
    setActiveTab('saved');
  };
  
  const handleDeleteSimulation = async (id: string) => {
    await deleteSimulation.mutateAsync(id);
    toast.success('Simulatie verwijderd');
  };
  
  const handleViewSimulation = (simulation: any) => {
    setViewingSimulation(simulation);
  };
  
  // Loading state
  if (siteLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  
  if (!site) {
    return (
      <div className="p-6 text-center">
        <p>Site niet gevonden</p>
        <Button onClick={() => navigate('/crm/analyses/epse')} className="mt-4">
          Terug naar overzicht
        </Button>
      </div>
    );
  }
  
  const hasData = profileMode === 'synthetic' || (quarterData && quarterData.length > 0);
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/crm/analyses/epse')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{site.name}</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {site.organization?.name && <span>{site.organization.name}</span>}
            <span>•</span>
            <span>Limiet: {site.connection_kw_limit} kW</span>
            {site.tags.map(tag => (
              <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
            ))}
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate(`/crm/analyses/epse/${siteId}/report`)}>
          <FileText className="h-4 w-4 mr-2" />
          Rapport
        </Button>
      </div>
      
      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="input" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Input
          </TabsTrigger>
          <TabsTrigger value="simulation" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Simulatie
          </TabsTrigger>
          <TabsTrigger value="saved" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Opgeslagen
            {simulations && simulations.length > 0 && (
              <Badge variant="secondary" className="ml-1">{simulations.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>
        
        {/* Input Tab */}
        <TabsContent value="input">
          <EPSEInputPanel
            profileMode={profileMode}
            onProfileModeChange={setProfileMode}
            syntheticConfig={syntheticConfig}
            onSyntheticConfigChange={setSyntheticConfig}
            bessConfig={bessConfig}
            onBessConfigChange={setBessConfig}
            dispatchMode={dispatchMode}
            onDispatchModeChange={setDispatchMode}
            selectedSeason={selectedSeason}
            onSeasonChange={setSelectedSeason}
            onDataUpload={handleDataUpload}
            isUploading={uploadQuarterData.isPending}
            quarterDataCount={quarterData?.length || 0}
            annualKwh={site.annual_kwh_total ?? undefined}
            // NEW props for optional modules
            bessEnabled={bessEnabled}
            onBessEnabledChange={setBessEnabled}
            pvEnabled={pvEnabled}
            onPvEnabledChange={setPvEnabled}
            unitOverride={unitOverride}
            onUnitOverrideChange={setUnitOverride}
            detectedUnit={detectedUnit}
          />
        </TabsContent>
        
        {/* Simulation Tab */}
        <TabsContent value="simulation">
          {hasData ? (
            <EPSESimulationDashboard
              liveChartData={liveChartData}
              liveKpis={liveKpis}
              isCalculating={isCalculating}
              energyBalance={energyBalance}
              syntheticConfig={syntheticConfig}
              connectionLimit={connectionLimit}
              bessConfig={bessEnabled ? bessConfig : null}
              selectedSeason={selectedSeason}
              onSeasonChange={setSelectedSeason}
              peakKw={peakKw}
              onSave={handleSaveSimulation}
              isSaving={runSimulation.isPending}
              quarterData={quarterData}
              profileMode={profileMode}
            />
          ) : (
            <div className="text-center py-12">
              <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                Configureer eerst uw energieprofiel in het Input tabblad om de simulatie te starten.
              </p>
              <Button variant="outline" className="mt-4" onClick={() => setActiveTab('input')}>
                Ga naar Input
              </Button>
            </div>
          )}
        </TabsContent>
        
        {/* Saved Simulations Tab */}
        <TabsContent value="saved">
          {viewingSimulation ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">{viewingSimulation.name}</h2>
                <Button variant="outline" onClick={() => setViewingSimulation(null)}>
                  Terug naar overzicht
                </Button>
              </div>
              
              {viewingSimulation.kpis && (
                <EPSEKPICards kpis={viewingSimulation.kpis} />
              )}
              
              {viewingSimulation.results?.soc_trajectory && (
                <EPSESOCChart 
                  socTrajectory={viewingSimulation.results.soc_trajectory} 
                  bessConfig={viewingSimulation.bess_config} 
                />
              )}
            </div>
          ) : (
            <EPSESavedSimulations
              simulations={simulations}
              isLoading={simulationsLoading}
              onDelete={handleDeleteSimulation}
              onView={handleViewSimulation}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
